//
/* GS://PROTOCOLO
 * Arquivo: src/pages/FinancePage.js
 * Versão: 5.2 – DRE Final (Sem Warnings)
 * Impacto da Versão: [Limpeza de Código]
 * Data: 29/11/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - REMOVIDO: Importação não utilizada 'Info'.
 * - MANTIDO: Integração com serviço e layout DRE.
 */

import React, { useState, useEffect } from 'react';
import { 
    Calendar, 
    Printer, 
    TrendingUp, 
    TrendingDown, 
    PieChart,
    Loader
} from 'lucide-react';
import { fetchDashboardData } from '../services/financialDashboardService';

const FinancePage = () => {
    // Datas Padrão: DO INÍCIO DO ANO ATÉ HOJE
    const today = new Date();
    const firstDayOfYear = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
    const todayStr = today.toISOString().split('T')[0];
    
    const [startDate, setStartDate] = useState(firstDayOfYear);
    const [endDate, setEndDate] = useState(todayStr);
    const [loading, setLoading] = useState(true);
    
    const [dreData, setDreData] = useState({
        grossRevenue: 0,
        deductions: 0,
        netRevenue: 0,
        expenses: 0,
        operatingResult: 0,
        expenseBreakdown: []
    });

    const loadDRE = async () => {
        setLoading(true);
        try {
            // USAR O SERVIÇO QUE JÁ FUNCIONA NO DASHBOARD
            const result = await fetchDashboardData(startDate, endDate);
            
            // O serviço na versão 3.0 já retorna o objeto 'dreStats' pronto!
            if (result && result.dreStats) {
                setDreData(result.dreStats);
            } else {
                console.warn("Objeto dreStats não encontrado no retorno do serviço.");
            }

        } catch (error) {
            console.error("Erro ao carregar DRE:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDRE();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    const fmtMoney = (v) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    const handlePrint = () => window.print();

    return (
        <div className="p-8 bg-gray-50 min-h-screen font-sans">
            <style>{`
                @media print {
                    @page { margin: 0; size: auto; }
                    body * { visibility: hidden; }
                    .finance-report-container, .finance-report-container * { visibility: visible; }
                    .finance-report-container { 
                        position: absolute; left: 0; top: 0; width: 100%; 
                        margin: 0; padding: 20px; background: white; box-shadow: none !important;
                    }
                    .no-print { display: none !important; }
                }
            `}</style>

            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-8 no-print">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <PieChart className="text-brand-gold" /> DRE Gerencial
                    </h1>
                    <p className="text-sm text-gray-500">Demonstrativo de Resultado do Exercício</p>
                </div>
                
                <div className="flex items-center gap-3">
                    <div className="flex items-center bg-white p-2 rounded border border-gray-300 shadow-sm">
                        <Calendar className="w-4 h-4 text-gray-500 mr-2" />
                        <input 
                            type="date" 
                            value={startDate} 
                            onChange={e => setStartDate(e.target.value)} 
                            className="text-sm border-none focus:ring-0 text-gray-600 outline-none" 
                        />
                        <span className="text-gray-400 mx-2">a</span>
                        <input 
                            type="date" 
                            value={endDate} 
                            onChange={e => setEndDate(e.target.value)} 
                            className="text-sm border-none focus:ring-0 text-gray-600 outline-none" 
                        />
                    </div>
                    <button 
                        onClick={handlePrint}
                        className="bg-gray-800 text-white p-2 rounded hover:bg-black transition shadow-md"
                        title="Imprimir Relatório"
                    >
                        <Printer size={20} />
                    </button>
                </div>
            </div>

            {/* RELATÓRIO */}
            <div className="finance-report-container bg-white max-w-4xl mx-auto shadow-lg p-10">
                <div className="text-center mb-10 border-b border-gray-200 pb-6">
                    <h2 className="text-2xl font-bold text-gray-900 uppercase tracking-wide">Demonstrativo de Resultado</h2>
                    <p className="text-gray-500 mt-1">Período: {new Date(startDate).toLocaleDateString('pt-BR')} a {new Date(endDate).toLocaleDateString('pt-BR')}</p>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                        <Loader className="animate-spin mb-2" />
                        <p>Calculando resultados...</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {/* Receita */}
                        <div className="flex justify-between items-center py-2">
                            <span className="text-gray-600 font-medium">(+) Receita Operacional Bruta</span>
                            <span className="text-gray-800 font-bold">{fmtMoney(dreData.grossRevenue)}</span>
                        </div>
                        <div className="flex justify-between items-center py-1 text-sm text-red-500">
                            <span className="pl-4">(-) Descontos e Abatimentos</span>
                            <span>{fmtMoney(dreData.deductions)}</span>
                        </div>
                        <div className="border-b border-gray-300 my-2"></div>
                        <div className="flex justify-between items-center py-2 bg-gray-50 px-2 rounded">
                            <span className="text-gray-800 font-bold">(=) RECEITA LÍQUIDA</span>
                            <span className="text-blue-600 font-bold text-lg">{fmtMoney(dreData.netRevenue)}</span>
                        </div>

                        <div className="h-4"></div>

                        {/* Despesa */}
                        <div className="flex justify-between items-center py-2">
                            <span className="text-gray-600 font-medium">(-) Custos e Despesas Operacionais</span>
                            <span className="text-red-600 font-bold">{fmtMoney(dreData.expenses)}</span>
                        </div>
                        <div className="pl-6 space-y-1 mb-4">
                            {dreData.expenseBreakdown.map((item, idx) => (
                                <div key={idx} className="flex justify-between text-sm text-gray-500 border-b border-gray-100 last:border-0 py-1">
                                    <span>{item.name}</span>
                                    <span>{fmtMoney(item.value)}</span>
                                </div>
                            ))}
                            {dreData.expenseBreakdown.length === 0 && (
                                <p className="text-xs text-gray-400 italic">Nenhuma despesa lançada no período.</p>
                            )}
                        </div>

                        <div className="border-b-2 border-gray-800 my-4"></div>

                        {/* Resultado */}
                        <div className={`flex justify-between items-center py-4 px-4 rounded-lg ${dreData.operatingResult >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            <div className="flex items-center gap-3">
                                {dreData.operatingResult >= 0 ? <TrendingUp /> : <TrendingDown />}
                                <span className="font-extrabold text-xl">(=) RESULTADO DO EXERCÍCIO</span>
                            </div>
                            <span className="font-extrabold text-2xl">{fmtMoney(dreData.operatingResult)}</span>
                        </div>

                        {/* KPIs */}
                        <div className="grid grid-cols-2 gap-4 mt-8">
                            <div className="text-center p-4 border rounded bg-gray-50">
                                <p className="text-xs text-gray-500 uppercase">Margem Líquida</p>
                                <p className="font-bold text-gray-700">
                                    {dreData.netRevenue > 0 
                                        ? ((dreData.operatingResult / dreData.netRevenue) * 100).toFixed(2) 
                                        : 0}%
                                </p>
                            </div>
                            <div className="text-center p-4 border rounded bg-gray-50">
                                <p className="text-xs text-gray-500 uppercase">Peso das Despesas</p>
                                <p className="font-bold text-gray-700">
                                    {dreData.netRevenue > 0 
                                        ? ((dreData.expenses / dreData.netRevenue) * 100).toFixed(2) 
                                        : 0}%
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-10 text-center text-xs text-gray-400 border-t pt-4">
                    <p>Relatório gerado pelo Ecossistema GoldenSisters em {new Date().toLocaleString('pt-BR')}</p>
                </div>
            </div>
        </div>
    );
};

export default FinancePage;